// Type, Interface, and Styled Component Imports
import { HotelProximityInfo } from "./../SearchResultProps";

// Utility and Helper Imports
import { Storage } from "../../../../../utils/storage";
import { Constants } from "../../../../../@types/Constants";

export const sortHotelsByRate = (
  hotelList: any,
  rateKeyForSort: string,
  sortOrder: "asc" | "desc" | string
) => {
  return hotelList.sort((a, b) => {
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const aValue =
      a[rateKeyForSort] === null ? Number.MAX_VALUE : a[rateKeyForSort]!;
    const bValue =
      b[rateKeyForSort] === null ? Number.MAX_VALUE : b[rateKeyForSort]!;

    if (sortOrder === "asc") {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });
};

function getBrandsToFilter(brandCode: string): string[] {
  switch (brandCode) {
    case "sig":
    case "sig inn":
      return ["sig", "sig inn"];
    case "rlis":
    case "red lion hotel":
      return ["rlis", "red lion hotel"];
    default:
      return [brandCode];
  }
}

export const sortAndFilterByBrand = (
  hotelList: HotelProximityInfo[],
  brand: any
) => {
  const brandCode = brand.brand_code?.toLowerCase() ?? "";
  const brandsToFilter = getBrandsToFilter(brandCode);
  const brandFilteredHotels = hotelList.filter((h) =>
    brandsToFilter.includes(h?.brand?.code?.toLowerCase())
  );
  const otherHotels = hotelList.filter(
    (h) => !brandsToFilter.includes(h?.brand?.code?.toLowerCase())
  );
  return [...brandFilteredHotels, ...otherHotels].sort(
    (a, b) =>
      parseFloat(String(a.rangeInMilesFromOrigin)) -
      parseFloat(String(b.rangeInMilesFromOrigin))
  );
};

export const sortHotelsByDistance = (
  hotelList: HotelProximityInfo[],
  sortOrder: "asc" | "desc" | string
) => {
  return hotelList.sort(
    (a, b) =>
      parseFloat(
        sortOrder === "asc"
          ? String(a.rangeInMilesFromOrigin)
          : String(b.rangeInMilesFromOrigin)
      ) -
      parseFloat(
        sortOrder === "asc"
          ? String(b.rangeInMilesFromOrigin)
          : String(a.rangeInMilesFromOrigin)
      )
  );
};

export const filterBrandBiasHotels = (hotelList: HotelProximityInfo[]) => {
  const brandBias = Storage.GetLocalStorageValue(Constants.BRAND_BIAS_KEY);
  const brandBiasData = JSON.parse(brandBias) as {
    brand_code?: string;
  };
  const brandBiasHotels: HotelProximityInfo[] = [];
  const nonBrandBiasHotels: HotelProximityInfo[] = [];
  hotelList.forEach((hotel: HotelProximityInfo) => {
    if (hotel?.brand?.code === brandBiasData?.brand_code) {
      brandBiasHotels.push(hotel);
    } else {
      nonBrandBiasHotels.push(hotel);
    }
  });
  return [brandBiasHotels, nonBrandBiasHotels];
};

export const getQueryStringBrands = (queryString: string | undefined) => {
  const searchParams = new URLSearchParams(queryString);
  const queryParams = Object.fromEntries(searchParams as any);
  type SearchParamsObject = { [key: string]: any };
  const queryParamsLowercase = Object.keys(queryParams).reduce((qp, key) => {
    qp[key.toLowerCase()] = queryParams[key];
    return qp;
  }, {} as SearchParamsObject);
  return queryParamsLowercase.brandcodes
    ? queryParamsLowercase.brandcodes.split(",")
    : [];
};

export const revelanceSortHotel = (
  hotelList: HotelProximityInfo[],
  searchString: string
) => {
  const proximityCityName = searchString.split(",")[0].trim();

  const { matchingSmartScore, matchingZeroSmartScore, othersHotel } =
    hotelList.reduce(
      (acc, hotel) => {
        if (
          hotel.address.cityName === proximityCityName ||
          hotel.secondaryCity === proximityCityName
        ) {
          hotel.smartScore
            ? acc.matchingSmartScore.push(hotel)
            : acc.matchingZeroSmartScore.push(hotel);
        } else {
          acc.othersHotel.push(hotel);
        }
        return acc;
      },
      {
        matchingSmartScore: [] as HotelProximityInfo[],
        matchingZeroSmartScore: [] as HotelProximityInfo[],
        othersHotel: [] as HotelProximityInfo[],
      }
    );
  matchingSmartScore.sort((a, b) => b.smartScore - a.smartScore);

  const normalizeName = (name: string) => {
    return name
      .replace(/[^\w\s]/g, "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
  };

  matchingZeroSmartScore.sort((a, b) => {
    if (a.rangeInMilesFromOrigin !== b.rangeInMilesFromOrigin) {
      return a.rangeInMilesFromOrigin - b.rangeInMilesFromOrigin;
    }
    return normalizeName(a.name).localeCompare(normalizeName(b.name));
  });

  othersHotel.sort((a, b) => {
    if (a.rangeInMilesFromOrigin !== b.rangeInMilesFromOrigin) {
      return a.rangeInMilesFromOrigin - b.rangeInMilesFromOrigin;
    }
    return normalizeName(a.name).localeCompare(normalizeName(b.name));
  });
  return [...matchingSmartScore, ...matchingZeroSmartScore, ...othersHotel];
};
